import { formatBp, formatMoney, margenBp, nivelMargen, recargoBp, type Centavos } from '@abastece/shared';
import { useT } from '../i18n/index.js';

const COLOR = {
  perdida: 'bg-rose-500/15 text-rose-300',
  bajo: 'bg-amber-500/15 text-amber-300',
  normal: 'bg-emerald-500/15 text-emerald-300',
} as const;

/**
 * Shows the margin over price, and the markup over cost underneath it.
 *
 * Both, because a shopkeeper says "I put 25% on it" meaning markup while the accounts mean
 * margin — buy at C$16, sell at C$20, and that is 20% one way and 25% the other. Showing one
 * number and not saying which is how a product ends up sold at a loss nobody notices.
 */
export function Margen({
  precio,
  costo,
  minimoBp,
  compacto = false,
}: {
  precio: Centavos;
  costo: Centavos;
  minimoBp: number;
  compacto?: boolean;
}) {
  const t = useT();
  if (precio <= 0 || costo <= 0) return <span className="text-xs text-slate-600">—</span>;

  const margen = margenBp(precio, costo);
  const nivel = nivelMargen(margen, minimoBp);

  if (compacto) {
    return (
      <span className={`tabular rounded px-1.5 py-0.5 text-xs font-medium ${COLOR[nivel]}`} data-margen-nivel={nivel}>
        {formatBp(margen)}
      </span>
    );
  }

  return (
    <div className="flex flex-col items-end gap-0.5">
      <span className={`tabular rounded px-2 py-0.5 text-sm font-semibold ${COLOR[nivel]}`}>
        {formatBp(margen)} {t('margen.margen')}
      </span>
      <span className="tabular text-xs text-slate-500">
        {t('margen.sobreCosto', {
          recargo: formatBp(recargoBp(precio, costo)),
          ganancia: formatMoney((precio - costo) as Centavos),
        })}
      </span>
      {nivel === 'perdida' && (
        <span className="text-xs font-medium text-rose-400">{t('margen.bajoCosto')}</span>
      )}
    </div>
  );
}

/**
 * A margin in its small pill (`Margen` compacto), or «—» with nothing to work it out from: no price,
 * or no cost known. On «Ofertas»' tables and under each price on «Precios/Costos».
 */
export const MargenDe = ({ precio, costo, minimoBp }: { precio: Centavos | null; costo: Centavos | null; minimoBp: number }) =>
  precio !== null && costo !== null ? <Margen precio={precio} costo={costo} minimoBp={minimoBp} compacto /> : <span className="text-xs text-slate-600">—</span>;
